/**
 * On-hold Orders List
 *
 * The "On hold" tab: every paused order at this location in one place —
 *   - operator ON_HOLD orders (send back into production any time), and
 *   - WAITING_STOCK orders (awaiting inventory; auto-resume when stock returns).
 * Filterable by reason. ON_HOLD is paused, NOT cancelled. Shopify-held orders show
 * a note instead of a Send-back button, since releasing a Shopify fulfillment hold
 * has to happen in Shopify (Phase 1 does not write to Shopify).
 *
 * Phase 104 (D-26 / D-27): manual orders held for art live here too. Their row
 * shows the art chips ("Art approval", "Awaiting artwork N", "Print file not
 * built · Retry"), the customer company and PO, the due pill, and Mark approved
 * for orders.create holders. Art holds are released only by Mark approved or by
 * the last file being built, never by Send back (the server refuses it, so the
 * button is not offered). `?reason=art` opens the tab on the Art filter (the
 * Order Queue's "Awaiting artwork" link).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Pause, AlertTriangle, RefreshCw, ExternalLink, ChevronRight, MailCheck, MailWarning, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { AWAITING_BLANK_HOLD_REASON, isArtHoldReason } from '@threadheads/shared';
import { playErrorBeep } from '@/lib/sounds';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { useOrganization } from '@/contexts/OrganizationContext';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import { useClientOrgLabels } from '@/hooks/useClientOrgLabels';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';
import { DuePill } from '@/features/manual-orders/components/DuePill';
import { useOnHoldOrders, useSendBackToProduction, useSetCustomerContacted } from '../hooks/useOnHold';
import { IssueFilterChips } from './IssueFilterChips';
import { IssueSearchInput } from './IssueSearchInput';
import { OrderDetailModal } from './OrderDetailModal';
import { IssueRowActionsMenu } from './IssueRowActionsMenu';

interface OnHoldListProps {
  locationId: string;
}

// 'partner_hold' = a Partner API client sent the order with its On Hold tag. The
// client releases it with its own API call; staff may still Send it back.
// 'art' = both manual-order art reasons (art_approval, awaiting_artwork).
type ReasonFilter = 'all' | 'waiting_for_stock' | 'waiting_for_customer' | 'manual' | 'partner_hold' | 'art';
const REASON_FILTERS: ReasonFilter[] = ['all', 'waiting_for_stock', 'waiting_for_customer', 'manual', 'partner_hold', 'art'];

/** The two manual-order art hold reasons (the shared ART_HOLD_REASONS list). */
const isArtHold = isArtHoldReason;

type OnHoldRow = ReturnType<typeof useOnHoldOrders>['orders'][number];
type ManualHold = NonNullable<OnHoldRow['manual']>;

function matchesReason(o: OnHoldRow, filter: ReasonFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'art') return isArtHold(o.hold_reason);
  return (o.hold_reason ?? 'manual') === filter;
}

/**
 * `?reason=art` (the Order Queue's "Awaiting artwork" link) opens on the Art
 * filter. Read once from the address bar so the list needs no router context.
 */
function initialReasonFilter(): ReasonFilter {
  if (typeof window === 'undefined') return 'all';
  const reason = new URLSearchParams(window.location.search).get('reason');
  return reason && (REASON_FILTERS as string[]).includes(reason) ? (reason as ReasonFilter) : 'all';
}

/** An approval is still outstanding on this manual order. */
function needsApproval(o: OnHoldRow, m: ManualHold): boolean {
  return o.hold_reason === 'art_approval' || (m.art_approval_required && !m.art_approved_at);
}

export function OnHoldList({ locationId }: OnHoldListProps) {
  const { t } = useTranslation('production');
  const { organization } = useOrganization();
  // BLU-3PL: cross-org chip for a 3PL owner viewing a client's held order.
  const { isCrossOrg, labelFor } = useClientOrgLabels();
  const { orders, isLoading, isError, refetch } = useOnHoldOrders(locationId);
  const sendBack = useSendBackToProduction();
  const setContacted = useSetCustomerContacted();
  // The contacted badge is a toggle only for a user the server lets toggle it:
  // setCustomerContacted checks orders.status_override (coordinator tier + CX).
  // Everyone else who can open this tab (Warehouse Staff) sees the same badge
  // as plain status.
  const { can } = useRole();
  const canToggleContacted = can('orders.status_override');
  const [filter, setFilter] = useState<ReasonFilter>(initialReasonFilter);
  const canApproveArt = can('orders.create');
  const [search, setSearch] = useState('');
  // The order whose detail modal is open (null = closed). canSendBack mirrors the
  // card's Send-back visibility so the modal offers it only for resumable holds.
  const [detail, setDetail] = useState<{ prodOrderId: string; canSendBack: boolean } | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (!matchesReason(o, filter)) return false;
      // Manual orders are also found by company and customer PO.
      const haystack = `${o.order_name} ${o.customer_name ?? ''} ${o.manual?.customer_company ?? ''} ${o.manual?.po_number ?? ''}`;
      if (q && !haystack.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [orders, filter, search]);
  const isSearching = search.trim().length > 0;

  const handleToggleContacted = (prodOrderId: string, currentlyContacted: boolean) => {
    setContacted.mutate(
      { prod_order_id: prodOrderId, contacted: !currentlyContacted },
      {
        onSuccess: () => {
          toast.success(
            currentlyContacted
              ? t('issues.onHold.markedNotContacted')
              : t('issues.onHold.markedContacted')
          );
        },
        onError: (error) => {
          playErrorBeep();
          toast.error(error.message || t('issues.onHold.contactedUpdateFailed'));
        },
      }
    );
  };

  const handleSendBack = (prodOrderId: string) => {
    sendBack.mutate(
      { prod_order_id: prodOrderId },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success(t('issues.onHold.sentBack'));
          } else {
            // Guarded no-op (e.g. the order was no longer ON_HOLD) — don't show a false success
            playErrorBeep();
            toast.error(t('issues.onHold.sendBackFailed'));
          }
        },
        onError: (error) => {
          playErrorBeep();
          toast.error(error.message || t('issues.onHold.sendBackFailed'));
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" aria-hidden="true" />
        <p className="mt-4 text-lg font-medium">{t('issues.onHold.failedToLoad')}</p>
        <button onClick={() => refetch()} className="mt-2 text-sm text-primary hover:underline">
          {t('issues.onHold.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search + reason filter pills */}
      <div className="flex flex-wrap items-center gap-3">
        <IssueSearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('issues.filters.searchPlaceholder')}
          ariaLabel={t('issues.filters.searchAriaLabel')}
          clearLabel={t('issues.filters.clearSearch')}
        />
        {isSearching && (
          <span className="text-sm text-muted-foreground">
            {t('issues.searchMatchCount', { count: filtered.length })}
          </span>
        )}
      </div>
      {/* Reason filter pills — shared IssueFilterChips, amber accent = the
          original inline classes byte-for-byte */}
      <IssueFilterChips<ReasonFilter>
        options={REASON_FILTERS.map((f) => ({
          value: f,
          label: t(`issues.onHold.filter.${f}`),
          count: orders.filter((o) => matchesReason(o, f)).length,
        }))}
        value={filter}
        onChange={setFilter}
        ariaLabel={t('issues.onHold.filterLabel')}
      />
      {filter === 'art' && (
        <p className="max-w-prose text-sm text-muted-foreground">{t('issues.onHold.art.flowHint')}</p>
      )}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Pause className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
          {isSearching ? (
            <p className="mt-4 text-lg font-medium">{t('issues.onHold.noResults')}</p>
          ) : (
            <>
              <p className="mt-4 text-lg font-medium">{t('issues.onHold.empty')}</p>
              <p className="text-sm text-muted-foreground">{t('issues.onHold.emptyHint')}</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => {
            // PR1: backend adds organization_id to the on-hold payload.
            const rowOrgId = (o as { organization_id?: string }).organization_id;
            const ageDays = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 86_400_000);
            const sending = sendBack.isPending && sendBack.variables?.prod_order_id === o.prod_order_id;
            const contactPending =
              setContacted.isPending && setContacted.variables?.prod_order_id === o.prod_order_id;
            const reasonKey = o.hold_reason ?? 'manual';
            // Phase 104: a manual order held for art. Only Mark approved or the
            // last print file being built releases it (never Send back, never
            // the stock cron), so it gets the art chips and no auto-resume badge.
            const artHold = isArtHold(o.hold_reason);
            const m = o.manual ?? null;
            const artManual = artHold && m ? m : null;
            // BLU-1506: waiting on a blank from a supplier is a system hold. Only
            // the box arriving releases it, so it never offers Send back. The
            // server keeps these rows off this list; this is the second guard.
            const blankHold = o.hold_reason === AWAITING_BLANK_HOLD_REASON;
            // An order re-queues automatically when it's waiting on stock: a WAITING_STOCK
            // row (is_on_hold=false), or an operator hold whose reason is 'waiting_for_stock'
            // (or that explicitly opted into auto-resume). Shopify-held orders never auto-resume.
            const willAutoResume =
              !artHold &&
              !blankHold &&
              !o.held_by_shopify &&
              (!o.is_on_hold || o.hold_auto_resume || o.hold_reason === 'waiting_for_stock');
            const canSendBack = !o.held_by_shopify && !artHold && !blankHold;
            const customerLine = m
              ? [m.customer_company, o.customer_name].filter((part) => !!part && part.trim().length > 0).join(' · ')
              : '';
            // A Partner API order's shopify_order_id is a made-up number, so a
            // Shopify link would open the wrong order (or none): show plain text.
            const shopifyUrl = o.from_partner_api || m
              ? null
              : getShopifyAdminOrderUrl(organization?.shopify_domain, o.shopify_order_id);
            const contactedBadge = (
              <Badge variant={o.cx_contacted_at ? 'success' : 'warning'} className="gap-1">
                {o.cx_contacted_at ? (
                  <MailCheck className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <MailWarning className="h-3 w-3" aria-hidden="true" />
                )}
                {o.cx_contacted_at
                  ? t('issues.onHold.contacted')
                  : t('issues.onHold.notContacted')}
              </Badge>
            );
            return (
              <Card key={o.prod_order_id} data-testid="on-hold-row" className="border-l-4 border-l-amber-500">
                <CardContent className="flex items-center justify-between gap-4 p-4 motion-reduce:transition-none">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {shopifyUrl ? (
                        <a
                          href={shopifyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-base font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                        >
                          {o.order_name}
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      ) : (
                        <span className="text-base font-semibold">{o.order_name}</span>
                      )}
                      {m && (
                        <Badge variant="default" className="text-primary-strong">
                          {t('orders.manual.pill')}
                        </Badge>
                      )}
                      {isCrossOrg(rowOrgId) && <ClientOrgChip label={labelFor(rowOrgId)} />}
                      {artManual ? (
                        <ArtHoldChips
                          row={o}
                          manual={artManual}
                          locationId={locationId}
                          canRetry={canApproveArt}
                        />
                      ) : (
                        <Badge variant="warning">{t(`issues.onHold.reason.${reasonKey}`)}</Badge>
                      )}
                      {m && <DuePill dueDate={m.due_date} />}
                      {o.held_by_shopify && (
                        <Badge variant="warning" className="gap-1">
                          <Pause className="h-3 w-3" aria-hidden="true" />
                          {t('issues.onHold.heldInShopify')}
                        </Badge>
                      )}
                      {willAutoResume && (
                        <Badge
                          variant="secondary"
                          className="gap-1"
                          title={t('issues.onHold.willAutoResume')}
                        >
                          <RefreshCw className="h-3 w-3" aria-hidden="true" />
                          {t('issues.onHold.autoResume')}
                        </Badge>
                      )}
                      {/* Clickable "customer contacted" status — toggles cx_contacted_at.
                          Amber (warning) for not-yet-contacted: it's a pending to-do, not an
                          error (red is reserved for operator-stop / OOS in the production palette).
                          Extra padding + hover give it a real hit area so it reads as a control,
                          not a static pill, next to the decorative reason/auto-resume badges.
                          Without orders.status_override it IS a static pill: same badge, no
                          button, so nobody is offered a toggle the server refuses. */}
                      {canToggleContacted ? (
                        <button
                          type="button"
                          onClick={() =>
                            handleToggleContacted(o.prod_order_id, o.cx_contacted_at != null)
                          }
                          // Scope disabled/pending to THIS row's mutation so toggling one badge
                          // doesn't grey out the list (mirrors the Send-back btn).
                          disabled={contactPending}
                          aria-pressed={o.cx_contacted_at != null}
                          aria-busy={contactPending}
                          className={cn(
                            'cursor-pointer rounded-md px-0.5 py-1 -my-0.5 transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
                            contactPending && 'motion-safe:animate-pulse'
                          )}
                          aria-label={t('issues.onHold.toggleContacted')}
                        >
                          {contactedBadge}
                        </button>
                      ) : (
                        contactedBadge
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-sm text-muted-foreground">
                      {customerLine || o.customer_name || t('issues.onHold.noCustomer')}
                      {m?.po_number && ` · ${t('orders.manual.po', { po: m.po_number })}`}
                      {o.total_line_items != null && ` · ${t('issues.onHold.itemCount', { count: o.total_line_items })}`}
                      {` · ${t('issues.onHold.heldFor', { days: ageDays })}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {/* BLU-842: the two location actions, one click from the row.
                        Move opens the same detail sheet as "Manage"; Split goes
                        to the resolve board. Hidden for roles that board rejects. */}
                    <IssueRowActionsMenu
                      prodOrderId={o.prod_order_id}
                      from="on-hold"
                      disabled={sending || contactPending}
                      onMove={() =>
                        setDetail({
                          prodOrderId: o.prod_order_id,
                          canSendBack,
                        })
                      }
                    />
                    {m && canApproveArt && (
                      // Phase 104 (D-21): a held manual order is not batched, so it opens for editing.
                      <Button asChild variant="ghost" className="h-14 gap-1">
                        <Link
                          to={`/production/orders/manual/${m.manual_order_id}`}
                          aria-label={t('orders.manual.editOrderLabel', { order: o.order_name })}
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                          {t('orders.manual.editOrder')}
                        </Link>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      className="h-14 gap-1"
                      onClick={() =>
                        setDetail({
                          prodOrderId: o.prod_order_id,
                          // Any non-Shopify-held order can be manually re-queued now —
                          // including WAITING_STOCK / waiting-for-stock holds (which also
                          // auto-resume). A Shopify fulfillment hold blocks send-back, and
                          // so does an art hold (Mark approved / the build release it).
                          canSendBack,
                        })
                      }
                    >
                      {t('issues.onHold.manage')}
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    {artHold ? (
                      artManual && canApproveArt && needsApproval(o, artManual) ? (
                        <MarkApprovedAction
                          manual={artManual}
                          locationId={locationId}
                        />
                      ) : null
                    ) : blankHold ? (
                      <span className="text-sm text-muted-foreground">
                        {t('issues.onHold.releasedWhenBlankArrives')}
                      </span>
                    ) : o.held_by_shopify ? (
                      <span className="text-sm text-muted-foreground">
                        {t('issues.onHold.releaseInShopify')}
                      </span>
                    ) : (
                      // Manual requeue is ALWAYS available for non-Shopify holds — even when
                      // the order will auto-resume on stock (the "Auto-resume" badge above
                      // signals that). Coordinators can send it back now instead of waiting.
                      <Button
                        className="h-14"
                        onClick={() => handleSendBack(o.prod_order_id)}
                        disabled={sending}
                      >
                        {sending
                          ? t('issues.onHold.sending')
                          : t(willAutoResume ? 'issues.onHold.sendBackNow' : 'issues.onHold.sendBack')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <OrderDetailModal
        open={detail !== null}
        onClose={() => setDetail(null)}
        issueId={null}
        context="on_hold"
        prodOrderId={detail?.prodOrderId ?? null}
        canSendBack={detail?.canSendBack ?? false}
      />
    </div>
  );
}

/**
 * The art chips of a manual order held for art: "Art approval" while an
 * approval is outstanding, "Awaiting artwork N" while N files are not uploaded,
 * and "Print file not built · Retry" while an uploaded file has no print file
 * yet. Retry is for orders.create holders (the procedure re-checks); others see
 * the chip without it.
 */
function ArtHoldChips({
  row,
  manual,
  locationId,
  canRetry,
}: {
  row: OnHoldRow;
  manual: ManualHold;
  locationId: string;
  canRetry: boolean;
}) {
  const { t } = useTranslation('production');
  const approval = needsApproval(row, manual);
  const missing = manual.missing_file_count;
  const unbuilt = manual.unbuilt_count;
  return (
    <>
      {approval && <Badge variant="warning">{t('issues.onHold.reason.art_approval')}</Badge>}
      {missing > 0 && (
        <Badge variant="warning" className="tabular-nums">
          {t('issues.onHold.art.awaitingCount', { count: missing })}
        </Badge>
      )}
      {unbuilt > 0 &&
        (canRetry ? (
          <RetryPrintFileChip orderName={row.order_name} manualOrderId={manual.manual_order_id} locationId={locationId} />
        ) : (
          <Badge variant="warning">{t('issues.onHold.art.notBuilt')}</Badge>
        ))}
      {/* awaiting_artwork with every file in and built: the release is a moment
          away; still say why it is here. */}
      {!approval && missing === 0 && unbuilt === 0 && (
        <Badge variant="warning">{t(`issues.onHold.reason.${row.hold_reason ?? 'awaiting_artwork'}`)}</Badge>
      )}
    </>
  );
}

function RetryPrintFileChip({
  orderName,
  manualOrderId,
  locationId,
}: {
  orderName: string;
  manualOrderId: string;
  locationId: string;
}) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();
  const retry = trpc.manualOrders.artwork.retryCompose.useMutation({
    onSettled: () => {
      void utils.productionOrders.issues.getOnHoldOrders.invalidate({ location_id: locationId });
    },
  });

  const handleRetry = () => {
    retry.mutate(
      { manual_order_id: manualOrderId },
      {
        onSuccess: () => toast.success(t('issues.onHold.art.building')),
        onError: (error) => {
          playErrorBeep();
          // Carry-over 1: branch on the tRPC code; the cause never arrives.
          const message =
            error.data?.code === 'TOO_MANY_REQUESTS'
              ? t('issues.onHold.art.busy')
              : error.message || t('issues.onHold.art.retryFailed');
          toast.error(message, { duration: Infinity });
        },
      },
    );
  };

  return (
    <Badge variant="warning" className="gap-1.5 pr-1">
      {t('issues.onHold.art.notBuilt')}
      <span aria-hidden="true">·</span>
      <button
        type="button"
        onClick={handleRetry}
        disabled={retry.isPending}
        aria-busy={retry.isPending}
        aria-label={t('issues.onHold.art.retryLabel', { order: orderName })}
        className="inline-flex items-center gap-1 rounded px-1 font-semibold underline underline-offset-2 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 dark:hover:bg-amber-900/40"
      >
        {retry.isPending && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
        {t('issues.onHold.art.retry')}
      </button>
    </Badge>
  );
}

/**
 * Mark approved (D-26 / D-27): orders.create holders only (the caller checks;
 * the procedure re-checks permission, owner and floor). Disabled ONLY while a
 * file is missing; an unbuilt print file never disables it (the order then
 * waits on the build and the toast says so).
 */
function MarkApprovedAction({
  manual,
  locationId,
}: {
  manual: ManualHold;
  locationId: string;
}) {
  const { t } = useTranslation('production');
  const utils = trpc.useUtils();
  const approve = trpc.manualOrders.holds.markApproved.useMutation({
    onSettled: () => {
      void utils.productionOrders.issues.getOnHoldOrders.invalidate({ location_id: locationId });
      void utils.productionOrders.issues.getCounts.invalidate();
      void utils.productionOrders.getOrderQueue.invalidate();
      void utils.productionOrders.getQueueCounts.invalidate();
      void utils.manualOrders.queue.badges.invalidate();
    },
  });
  const missingFile = manual.missing_file_count > 0;
  const hintId = `mark-approved-hint-${manual.manual_order_id}`;

  const handleApprove = () => {
    approve.mutate(
      { manual_order_id: manual.manual_order_id },
      {
        onSuccess: (result) => {
          if (result.released) {
            toast.success(t('issues.onHold.art.approved'));
            return;
          }
          // Approved, but another hold keeps it here: say which, never "released".
          const reason = result.holdReason;
          toast.info(
            reason === 'awaiting_artwork' && manual.missing_file_count === 0
              ? t('issues.onHold.art.approvedWaitingPrintFile')
              : t('issues.onHold.art.approvedStillHeld', {
                  reason: t(`issues.onHold.reason.${reason ?? 'manual'}`),
                }),
          );
        },
        onError: (error) => {
          playErrorBeep();
          toast.error(error.message || t('issues.onHold.art.approveFailed'), { duration: Infinity });
        },
      },
    );
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        className="h-14"
        onClick={handleApprove}
        disabled={missingFile || approve.isPending}
        aria-busy={approve.isPending}
        aria-describedby={missingFile ? hintId : undefined}
        title={missingFile ? t('issues.onHold.art.missingFileFirst') : undefined}
      >
        {approve.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
        {approve.isPending ? t('issues.onHold.art.approving') : t('issues.onHold.art.markApproved')}
      </Button>
      {missingFile && (
        <span id={hintId} className="text-xs text-muted-foreground">
          {t('issues.onHold.art.missingFileFirst')}
        </span>
      )}
    </div>
  );
}
